import React from "react";
import { connect } from "react-redux";
import { Dropdown, Grid, Loader, Dimmer, Button } from "semantic-ui-react";
import DownloadButton from "@buttons/DownloadButton";
import {
  getCabinetTypesRaw,
  getManufacturersRaw,
  loadCabinetType,
  updateCabinetType,
} from "@actions/index";
import {
  cabinetTypeDownload,
  getAssetTable,
  getAssetsDetailsDownload,
} from "@api/egm";
import { setCreateAssetModal, showSuccessAlert } from "@actions/modals";
import AssetMainTable from "./AssetMainTable";
import BaseSettings from "../systemAdmin/BaseSettings";
import OptionsList from "../systemAdmin/OptionsList";
import AssetDetailView from "./AssetDetailView";
import { ASSET_STATUS_FILTER_DATA } from "@helpers/constant";
import { handleDownload } from "@helpers/downloads";

const CABINET_TYPE_TABLE_COLUMNS = [
  {
    key: "code",
    label: "Code",
    accessor: (row) => row.cabinetCode ?? "-",
  },
  { key: "name", label: "Name", accessor: "cabinetName" },
  { key: "manufacturer", label: "Manufacturer", accessor: "manufacturerName" },
  {
    key: "defaultOwnership",
    label: "Default Ownership",
    accessor: (row) => row.defaultOwnership ?? "-",
  },
  {
    key: "installed",
    label: "Installed",
    accessor: (row) => row.installedCount ?? "-",
  },
  {
    key: "notInstalled",
    label: "Not Installed",
    accessor: (row) => row.notInstalledCount ?? "-",
  },
  { key: "pendingScrapped", label: "Pending Scrapped", accessor: "pendingScrappedCount" },
  { key: "sold", label: "Sold", accessor: "soldCount" },
];

class CabinetTypeView extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      operatorId: null,
      cabinetTypes: [],
      manufacturers: [],
      selectedManufacturerId: "ALL",
      loading: false,
      saving: false,
      selectedCabinetType: null,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      selectedNode: null,
      grid: null,
      doQuestion: false,
      doDelete: false,
      doUpdate: false,
      questionText: "",
      changed: [],
      showDetail: false,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { createAssetModal } = this.props;
    const prev = prevProps.createAssetModal ?? {};
    if (
      prev.open &&
      !createAssetModal?.open &&
      createAssetModal?.lastCreatedType === "CT"
    ) {
      this.getCabinetTypes();
      this.props.setCreateAssetModal({ lastCreatedType: null });
    }
  }

  componentDidMount() {
    const { operators } = this.props;
    if (operators.length === 1 && operators[0].value !== -1) {
      this.setState(
        { operatorId: operators[0].value },
        this.getCabinetTypes,
      );
    }
  }

  onOperatorChange = (e, { value }) => {
    this.setState(
      {
        operatorId: value,
        manufacturers: [],
        selectedManufacturerId: "ALL",
        selectedCabinetType: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      },
      () => {
        if (value) {
          this.getCabinetTypes();
        }
      },
    );
  };

  getAssetTableData = (cabinetId, statusOption) => {
    const { operatorId } = this.state;

    return getAssetTable(
      operatorId,
      -1,
      -1,
      cabinetId,
      -1,
      -1,
      statusOption,
      -1,
      0,
      "#",
      -1,
      -1,
      -1,
      true,
    );
  };

  handleAssetStatusOptionChange = (option) => {
    const { selectedCabinetType } = this.state;
    if (!selectedCabinetType) return;

    this.setState(
      { assetDetailStatusOption: option, loading: true },
      () => {
        this.getAssetTableData(selectedCabinetType.cabinetId, option)
          .then((tableData) => {
            this.setState({
              assetDetailAssets: tableData.assets || [],
              loading: false,
            });
          })
          .catch(() => this.setState({ loading: false }));
      },
    );
  };

  getCabinetTypes = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      Promise.all([getCabinetTypesRaw(operatorId), getManufacturersRaw(operatorId)])
        .then(([cabinetTypesData, manufacturersData]) => {
          this.setState({
            cabinetTypes: Array.isArray(cabinetTypesData) ? cabinetTypesData : [],
            manufacturers: Array.isArray(manufacturersData) ? manufacturersData : [],
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  onManufacturerFilterChange = (e, { value }) => {
    this.setState({
      selectedManufacturerId: value,
      selectedCabinetType: null,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    });
  };

  handleNew = (selectedOptions, selectedNode, grid = null) => {
    this.setState({
      selectedOptions: selectedOptions.main
        ? selectedOptions
        : { main: selectedOptions, ownership: [], operational: [] },
      changed: [],
      selectedNode,
      grid,
    });
  };

  onSelectRow = (item) => {
    const defaultStatus = ASSET_STATUS_FILTER_DATA[0].value;
    this.setState({
      loading: true,
      showDetail: true,
      assetDetailStatusOption: defaultStatus,
    });
    Promise.all([
      loadCabinetType(item.cabinetId),
      this.getAssetTableData(item.cabinetId, defaultStatus),
    ])
      .then(([response, tableData]) => {
        const { doQuestion, doDelete, doUpdate, questionText } = response;
        this.setState(
          {
            doQuestion,
            doDelete,
            doUpdate: doUpdate ?? false,
            questionText,
            selectedCabinetType: item,
            assetDetailAssets: tableData.assets || [],
          },
          () => {
            this.handleNew(
              response.assetUI
                ? response.assetUI
                : {
                    main: response.options || response.main || [],
                    operational: [],
                    ownership: [],
                  },
              item,
              response.grid,
            );
            this.setState({ loading: false });
          },
        );
      })
      .catch(() =>
        this.setState({ loading: false }),
      );
  };

  onNew = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.props.setCreateAssetModal({
      open: true,
      type: "CT",
      operatorId,
      name: "New Cabinet Type",
    });
  };

  onSave = () => {
    const { operatorId, selectedCabinetType, selectedOptions, doUpdate } =
      this.state;
    const { showSuccessAlert } = this.props;
    if (!operatorId || !selectedCabinetType) return;
    const cabinetId = selectedCabinetType
      ? selectedCabinetType.cabinetId
      : 0;
    const changed = this.getAll(selectedOptions);

    this.setState({ saving: true }, () => {
      updateCabinetType(
        cabinetId,
        operatorId,
        changed,
        !!doUpdate,
        () => this.getCabinetTypes(),
      )
        .then(() => {
          this.setState({ saving: false }, () => {
            showSuccessAlert(
              cabinetId
                ? "Cabinet type updated successfully."
                : "Cabinet type created successfully.",
            );
          });
        })
        .catch(() => this.setState({ saving: false }));
    });
  };

  handleDownload = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      cabinetTypeDownload(operatorId).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  handleAssetDetailDownload = () => {
    const {
      operatorId,
      selectedCabinetType,
      assetDetailStatusOption,
    } = this.state;
    if (!operatorId || !selectedCabinetType) return;

    this.setState({ loading: true }, () => {
      getAssetsDetailsDownload(
        operatorId,
        -1,
        -1,
        selectedCabinetType.cabinetId,
        -1,
        -1,
        assetDetailStatusOption,
        -1,
        0,
        "#",
        -1,
        -1,
        -1,
        true,
      ).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  render() {
    const {
      cabinetTypes,
      manufacturers,
      loading,
      saving,
      selectedCabinetType,
      selectedOptions,
      operatorId,
      selectedManufacturerId,
    } = this.state;

    const canSave =
      !!selectedCabinetType &&
      this.isAllValid(
        selectedOptions || { main: [], ownership: [], operational: [] },
      ) &&
      this.state.changed.length > 0;

    const manufacturerOptions = [
      { key: "all", value: "ALL", text: "All" },
      ...[...manufacturers]
        .sort((a, b) =>
          (a.manufacturerName || "").localeCompare(b.manufacturerName || ""),
        )
        .map((manufacturer) => ({
          key: manufacturer.manufacturerId,
          value: manufacturer.manufacturerId,
          text: manufacturer.manufacturerName,
        })),
    ];

    const filteredCabinetTypes = cabinetTypes.filter((cabinetType) => {
      const matchesManufacturerFilter =
        selectedManufacturerId === "ALL" ||
        Number(cabinetType.manufacturerId) === Number(selectedManufacturerId);

      return matchesManufacturerFilter;
    });

    return (
      <div className="flex-stretch gs-flex-container-vertical height-100">
        <Grid className="height-100 flex-stretch">
          <Grid.Row
            className={`flex-stretch ${this.state.showDetail ? "h-50" : "h-100"}`}
          >
            <Grid.Column width={12} className="height-100 flex">
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <div className="mb-0_5">
                <Dropdown
                  placeholder="Select operator"
                  selection
                  options={this.props.operators}
                  value={operatorId || ""}
                  onChange={this.onOperatorChange}
                  disabled={!this.props.operators.length}
                />
                <Dropdown
                  className="ml-1"
                  placeholder="Filter manufacturer"
                  selection
                  options={manufacturerOptions}
                  value={selectedManufacturerId}
                  onChange={this.onManufacturerFilterChange}
                  disabled={!operatorId}
                />
                <Button
                  className="ml-0_5"
                  icon="expand"
                  basic={!this.state.showDetail}
                  color="grey"
                  secondary
                  onClick={() =>
                    this.setState({ showDetail: !this.state.showDetail })
                  }
                  size="mini"
                  disabled={!selectedCabinetType}
                />
              </div>
              <div className="mb-0_5 flex-stretch">
                <AssetMainTable
                  columns={CABINET_TYPE_TABLE_COLUMNS}
                  data={filteredCabinetTypes}
                  rowKey="cabinetId"
                  selectedRowId={selectedCabinetType?.cabinetId}
                  onSelectRow={this.onSelectRow}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4} className="height-100 flex">
              <div className="flex-start">
                <Button
                  color="green"
                  content="New"
                  onClick={this.onNew}
                  disabled={!operatorId}
                  loading={loading}
                />
                <Button
                  primary
                  content="Save"
                  onClick={this.onSave}
                  disabled={!canSave}
                  loading={saving}
                />
                <DownloadButton
                  disabled={!this.state.operatorId}
                  caption="Export"
                  onClick={this.handleDownload}
                  loading={loading}
                />
              </div>
              <div className="flex-stretch height-100 p-1 border-grey-1 flex">
                <OptionsList
                  options={this.mapChanged(
                    (selectedOptions && selectedOptions.main) || [],
                  )}
                  onChange={this.handleChangeValue}
                />
              </div>
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
            </Grid.Column>
          </Grid.Row>
          {this.state.showDetail && (
            <Grid.Row className="flex-stretch h-50">
              <Grid.Column width={16}>
                <AssetDetailView
                  statusOption={this.state.assetDetailStatusOption}
                  assets={this.state.assetDetailAssets}
                  loading={this.state.loading}
                  operatorId={this.state.operatorId}
                  detailExportDisabled={!this.state.selectedCabinetType}
                  onStatusOptionChange={this.handleAssetStatusOptionChange}
                  onDownloadClick={this.handleAssetDetailDownload}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  operators: state.operators?.filter((x) => x.value !== -1) ?? [],
  createAssetModal: state.createAssetModal,
});

export default connect(mapStateToProps, {
  setCreateAssetModal,
  showSuccessAlert,
})(CabinetTypeView);
